import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";
import { useTranslation } from "react-i18next";

export default function AdminEnterPriceQuotes() {
  const { t } = useTranslation();

  const [effectiveDate, setEffectiveDate] = useState("");
  const [vendorId, setVendorId] = useState(null);
  const [walletId, setWalletId] = useState("");
  const [batch, setBatch] = useState("");
  const [priceBeforeTax, setPriceBeforeTax] = useState("");
  const [vat, setVat] = useState("");
  const [priceAfterTax, setPriceAfterTax] = useState("");

  const [vendors, setVendors] = useState([]);
  const [wallets, setWallets] = useState([]);

  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/vendors`);
      const uniqueVendorsMap = {};
      res.data.forEach((vendor) => {
        if (!uniqueVendorsMap[vendor.user_id]) {
          uniqueVendorsMap[vendor.user_id] = vendor;
        }
      });
      setVendors(Object.values(uniqueVendorsMap));
    } catch (err) {
      console.error("❌ Failed to fetch vendors");
    }
  };

  const fetchWallets = async (id) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/all-wallets-of/${id}`);
      setWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch wallets", err);
      setWallets([]);
    }
  };

  useEffect(() => {
    if (vendorId !== null) {
      fetchWallets(vendorId);
      setWalletId("");
    }
  }, [vendorId]);

  const handleSubmit = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/enter-price-quote`, {
        effective_date: effectiveDate,
        vendor_id: vendorId,
        wallet_id: parseInt(walletId),
        batch: parseInt(batch),
        price_before_tax: parseFloat(priceBeforeTax),
        VAT: parseFloat(vat),
        price_after_tax: parseFloat(priceAfterTax),
      });

      setMessage("✅ " + t("admin_enter_price_quote.messages.success"));
      setLastAction("submit");

      setEffectiveDate("");
      setVendorId(null);
      setWalletId("");
      setBatch("");
      setPriceBeforeTax("");
      setVat("");
      setPriceAfterTax("");
    } catch (err) {
      console.error("❌ Error adding quote:", err);
      setMessage("❌ " + t("admin_enter_price_quote.messages.failure"));
      setLastAction("submit");
    }
  };

  return (
    <StandardScreen
      background="default"
      topNav={<TopNav title={t("admin_enter_price_quote.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={{ maxWidth: 720, width: "100%", margin: "0 auto" }}>
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 700 }}>
                  {t("admin_enter_price_quote.title")}
                </h1>

                <FormField label={t("admin_enter_price_quote.fields.effective_date.label")}>
                  <TextInput
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                  />
                </FormField>

                <FormField label={t("admin_enter_price_quote.fields.select_vendor.label")}>
                  <div>
                    <Select
                      classNamePrefix="react-select"
                      options={vendors.map((v) => ({
                        value: v.user_id,
                        label: `${v.fullname} (${v.user_id})`,
                      }))}
                      value={
                        vendorId !== null
                          ? {
                              value: vendorId,
                              label: `${vendors.find((v) => v.user_id === vendorId)?.fullname} (${vendorId})`,
                            }
                          : null
                      }
                      onChange={(option) => {
                        const id = option?.value ?? null;
                        setVendorId(id);
                        setMessage("");
                        setLastAction("");
                      }}
                      placeholder={t("admin_enter_price_quote.fields.select_vendor.placeholder")}
                    />
                  </div>
                </FormField>

                {vendorId && (
                  <FormField label={t("admin_enter_price_quote.fields.select_wallet.label")}>
                    <SelectInput
                      value={walletId}
                      onChange={(e) => setWalletId(e.target.value)}
                    >
                      <option value="">
                        {t("admin_enter_price_quote.fields.select_wallet.placeholder")}
                      </option>
                      {wallets.map((w) => (
                        <option key={w.wallet_id} value={w.wallet_id}>
                          {(w.walletname || t("admin_enter_price_quote.fields.select_wallet.empty_name"))} ({w.wallet_id})
                        </option>
                      ))}
                    </SelectInput>
                  </FormField>
                )}

                <FormField label={t("admin_enter_price_quote.fields.batch.label")}>
                  <TextInput type="number" value={batch} onChange={(e) => setBatch(e.target.value)} />
                </FormField>

                <FormField label={t("admin_enter_price_quote.fields.price_before_tax.label")}>
                  <TextInput type="number" step="0.01" value={priceBeforeTax} onChange={(e) => setPriceBeforeTax(e.target.value)} />
                </FormField>

                <FormField label={t("admin_enter_price_quote.fields.vat.label")}>
                  <TextInput type="number" step="0.01" value={vat} onChange={(e) => setVat(e.target.value)} />
                </FormField>

                <FormField label={t("admin_enter_price_quote.fields.price_after_tax.label")}>
                  <TextInput type="number" step="0.01" value={priceAfterTax} onChange={(e) => setPriceAfterTax(e.target.value)} />
                </FormField>

                <Button
                  onClick={() => {
                    setLastAction("submit");
                    handleSubmit();
                  }}
                  disabled={
                    !effectiveDate ||
                    !vendorId ||
                    !walletId ||
                    !batch ||
                    !priceBeforeTax ||
                    !vat ||
                    !priceAfterTax ||
                    (lastAction === "submit" &&
                      (message.startsWith("✅") || message.startsWith("❌")))
                  }
                  fullWidth
                >
                  {lastAction === "submit"
                    ? message.startsWith("✅")
                      ? t("admin_enter_price_quote.status.success_short")
                      : message.startsWith("❌")
                      ? `${t("admin_enter_price_quote.status.error_short")}: ${message.replace("❌ ", "")}`
                      : t("admin_enter_price_quote.actions.submit")
                    : t("admin_enter_price_quote.actions.submit")}
                </Button>
              </Stack>
            </Card>

            <StatusMessage message={message} />
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}